import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import { ASSET_SOURCE_PIXELS_PER_METRE } from './asset-source-scale.js';
import { ENTITY_SPRITE_MARGIN_FRACTION, ENTITY_SPRITE_SPECS_V1 } from './entity-sprite-specs-v1.js';

/**
 * Size contract for skill visual assets. A skill is drawn as reusable role
 * assets (body, area, impact, trail, cast) sized from the authoritative
 * gameplay value named in `sizeSource`; nothing here feeds collision, hit,
 * timing or pricing code. Art uses the entity sprite scale and margin, is
 * pivoted as stated, and faces up (−Y) when it has a facing.
 */
export type SkillAssetRoleV1 = 'BODY' | 'AREA' | 'IMPACT' | 'TRAIL' | 'CAST';

export interface SkillAssetSpecV1 {
  readonly role: SkillAssetRoleV1;
  /** SCALE_TO_RADIUS stretches uniformly so `referenceRadiusM` matches the live radius; FIXED draws at source scale. */
  readonly sizeMode: 'SCALE_TO_RADIUS' | 'FIXED';
  readonly referenceRadiusM: number;
  /** TRAIL only: length behind the head at the reference radius; scales with it. */
  readonly referenceLengthM?: number;
  readonly facing: 'NONE' | 'VELOCITY' | 'CAST_FACING';
  readonly sizeSource: string;
  readonly lifetimeSource: string;
  readonly canvasPx: readonly [number, number];
  readonly pivotPx: readonly [number, number];
}

/** Reference radius for templates whose live radius comes from a per-skill value. */
const AREA_REFERENCE_RADIUS_M = 1;
/** Presentation-only trail length, in body radii. */
const TRAIL_LENGTH_RADII = 4;

const px = (metres: number): number => metres * ASSET_SOURCE_PIXELS_PER_METRE;

const circle = (role: SkillAssetRoleV1, sizeMode: SkillAssetSpecV1['sizeMode'], referenceRadiusM: number,
  facing: SkillAssetSpecV1['facing'], sizeSource: string, lifetimeSource: string): SkillAssetSpecV1 => {
  const size = Math.ceil(2 * px(referenceRadiusM) * (1 + ENTITY_SPRITE_MARGIN_FRACTION));
  return Object.freeze<SkillAssetSpecV1>({ role, sizeMode, referenceRadiusM, facing, sizeSource, lifetimeSource,
    canvasPx: [size, size], pivotPx: [size / 2, size / 2] });
};

const trail = (referenceRadiusM: number): SkillAssetSpecV1 => {
  const referenceLengthM = referenceRadiusM * TRAIL_LENGTH_RADII;
  const margin = px(referenceRadiusM) * ENTITY_SPRITE_MARGIN_FRACTION;
  const head = px(referenceRadiusM) + margin;
  const width = Math.ceil(2 * head);
  return Object.freeze<SkillAssetSpecV1>({ role: 'TRAIL', sizeMode: 'SCALE_TO_RADIUS', referenceRadiusM, referenceLengthM,
    facing: 'VELOCITY', sizeSource: 'AttackSnapshot.radiusM of the body it follows',
    lifetimeSource: 'the body it follows', canvasPx: [width, Math.ceil(head + px(referenceLengthM) + margin)], pivotPx: [width / 2, head] });
};

const body = ENTITY_SPRITE_SPECS_V1.PROJECTILE;

export const SKILL_ASSET_SPECS_V1: Readonly<Record<SkillAssetRoleV1, SkillAssetSpecV1>> = Object.freeze({
  // Projectiles and orbiting orbs; the same circle as the entity PROJECTILE template.
  BODY: circle('BODY', 'SCALE_TO_RADIUS', body.radiusM, 'VELOCITY',
    'AttackSnapshot.radiusM (projectile radius)', 'while the attack object exists'),
  AREA: circle('AREA', 'SCALE_TO_RADIUS', AREA_REFERENCE_RADIUS_M, 'NONE',
    'AttackSnapshot.radiusM (persistent area radius)', 'while the attack object exists'),
  IMPACT: circle('IMPACT', 'SCALE_TO_RADIUS', AREA_REFERENCE_RADIUS_M, 'NONE',
    'skill_impact.radiusM (instant circle radius)', 'one-shot presentation effect'),
  TRAIL: trail(body.radiusM),
  CAST: circle('CAST', 'FIXED', PLAYER_RADIUS_M, 'CAST_FACING',
    'PLAYER_RADIUS_M (caster circle)', 'one-shot on the cast event'),
});

/**
 * An asset key tagged with the role it was authored for. Each profile slot
 * only accepts its own role, and the asset catalog must reject a key whose
 * catalogued role differs from `role`.
 */
export interface SkillVisualBindingV1<R extends SkillAssetRoleV1 = SkillAssetRoleV1> {
  readonly role: R;
  readonly assetKey: string;
}

/** Binds role assets to one V3 stage; keyed by `stageId` in the profile. */
export type StageVisualProfileV1 = { readonly [R in Exclude<SkillAssetRoleV1, 'CAST'>]?: SkillVisualBindingV1<R> };

/**
 * One servable asset. The catalog records the role each key was authored
 * for; a binding whose role differs is rejected, never drawn.
 */
export interface SkillAssetCatalogEntryV1 {
  readonly assetKey: string;
  readonly role: SkillAssetRoleV1;
  /** Path under `/assets/skills/`. */
  readonly file: string;
}

/** Presentation profile resolved from `visualProfileId`; asset keys never travel on the wire. */
export interface SkillVisualProfileV1 {
  readonly profileId: string;
  readonly cast?: SkillVisualBindingV1<'CAST'>;
  readonly stages: Readonly<Record<string, StageVisualProfileV1>>;
}

const round = (value: number): string => String(Math.round(value * 100) / 100);

/** Artist guide: reference outline, pivot cross, facing arrow and label on a transparent canvas. */
export function skillAssetTemplateSvg(asset: SkillAssetSpecV1): string {
  const [w, h] = asset.canvasPx; const [cx, cy] = asset.pivotPx;
  const r = px(asset.referenceRadiusM); const stroke = round(w * 0.006);
  const cross = w * 0.04;
  const length = asset.referenceLengthM === undefined ? '' : ` length=${asset.referenceLengthM}m (${round(px(asset.referenceLengthM))}px)`;
  const heading = `${asset.role} ${asset.sizeMode} facing=${asset.facing}`;
  const size = `r=${asset.referenceRadiusM}m (${round(r)}px)${length} canvas=${w}x${h}px @${ASSET_SOURCE_PIXELS_PER_METRE}px/m`;
  const font = w * 0.026;
  const outline = asset.referenceLengthM === undefined
    ? `  <circle cx="${round(cx)}" cy="${round(cy)}" r="${round(r)}" fill="none" stroke="#1c7ed6" stroke-width="${stroke}"/>\n`
    : `  <circle cx="${round(cx)}" cy="${round(cy)}" r="${round(r)}" fill="none" stroke="#adb5bd" stroke-width="${stroke}" stroke-dasharray="12 8"/>\n`
      + `  <path d="M${round(cx - r)} ${round(cy)} L${round(cx)} ${round(cy + px(asset.referenceLengthM))} L${round(cx + r)} ${round(cy)}" fill="none" stroke="#1c7ed6" stroke-width="${stroke}"/>\n`;
  const arrow = asset.facing === 'NONE' ? '' : [
    `  <path d="M${round(cx)} ${round(cy - r * 0.35)} V${round(cy - r * 0.95)}" stroke="#e03131" stroke-width="${round(w * 0.008)}" fill="none"/>`,
    `  <path d="M${round(cx - r * 0.12)} ${round(cy - r * 0.8)} L${round(cx)} ${round(cy - r * 0.95)} L${round(cx + r * 0.12)} ${round(cy - r * 0.8)}" stroke="#e03131" stroke-width="${round(w * 0.008)}" fill="none"/>`,
  ].join('\n') + '\n';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <title>${heading} ${size}</title>
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" fill="none" stroke="#adb5bd" stroke-dasharray="8 6"/>
${outline}${arrow}  <path d="M${round(cx - cross)} ${round(cy)} H${round(cx + cross)} M${round(cx)} ${round(cy - cross)} V${round(cy + cross)}" stroke="#212529" stroke-width="${round(w * 0.004)}"/>
  <text x="${round(w / 2)}" y="${round(h - font * 2.4)}" font-family="sans-serif" font-size="${round(font)}" text-anchor="middle" fill="#495057">${heading}</text>
  <text x="${round(w / 2)}" y="${round(h - font)}" font-family="sans-serif" font-size="${round(font)}" text-anchor="middle" fill="#495057" textLength="${round(w * 0.94)}" lengthAdjust="spacingAndGlyphs">${size}</text>
</svg>
`;
}
